import {
  AcademicCapIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpTrayIcon,
  BanknotesIcon,
  BookOpenIcon,
  BuildingLibraryIcon,
  PencilSquareIcon,
  UserGroupIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { useContext, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { UserContext } from "../../App";

// InstructorIcon as a separate React component
function InstructorIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="currentColor"
      viewBox="0 0 512 512"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      {/* simplified and cleaned path for demonstration */}
      <path d="M302 12H13c-7 0-13 6-13 13v214c0 7 6 13 13 13h284l-34-21H26V38h263v127c4 1 8 2 12 5l14 9V25c0-7-6-13-13-13z" />
      <circle cx="420" cy="81" r="38" />
      <path d="M512 174c-1-23-19-43-43-43h-19c-18 46-11 29-22 58l5-24c0-2-1-4-2-6l-7-13 7-12c0-1-1-2-2-2h-18c-1 0-2 1-2 2s1 2 2 3l7 12-7 13c-1 2-1 3 0 5l5 24c-3-8-21-53-23-58h-18c-24 0-43 19-43 43-1 20 0 3-1 34l-37-23c-9-5-20-3-25 6-5 8-3 20 6 25l64 40c12 7 28-1 28-15 0-7 0-58 0-66 0-2 2-4 4-4s4 2 4 4c-1 87-1 112-1 305 0 12 10 22 22 22s22-10 22-22V305h9v174c0 12 10 22 22 22s22-10 22-22v-305c0-2 2-4 4-4s4 2 4 4l1 133c0 10 8 18 18 18 0 0 0 0 0 0 10-1 18-8 18-18v-133z" />
    </svg>
  );
}

const globalAdminNavMenu = [
  {
    label: "Organizations",
    route: "/organizations",
    icon: <BuildingLibraryIcon className="h-5 w-5" />,
    submenu: [],
  },
];

const businessAdminNavMenu = [
  {
    label: "Organization Admins",
    route: "/users",
    icon: <UserGroupIcon className="h-5 w-5" />,
    submenu: [],
  },
  {
    label: "Students",
    route: "/students",
    icon: <AcademicCapIcon className="h-5 w-5" />,
    submenu: [],
  },
  {
    label: "Classrooms",
    route: "/classrooms",
    icon: <BookOpenIcon className="h-5 w-5" />,
    submenu: [],
  },
  {
    label: "Instructors",
    route: "/instructors",
    icon: <InstructorIcon className="h-5 w-5" />,
    submenu: [],
  },
  {
    label: "Assignments",
    route: "/assignments",
    icon: <PencilSquareIcon className="h-5 w-5" />,
    submenu: [],
  },
  {
    label: "Import Jobs",
    route: "/import-jobs",
    icon: <ArrowUpTrayIcon className="h-5 w-5" />,
    submenu: [],
  },
  {
    label: "Organization",
    route: "/organization",
    icon: <BuildingLibraryIcon className="h-5 w-5" />,
    submenu: [],
  },
];

const businessInstructorNavMenu = [
  {
    label: "Students",
    route: "/students",
    icon: <AcademicCapIcon className="h-5 w-5" />,
    submenu: [],
  },
  {
    label: "Classrooms",
    route: "/classrooms",
    icon: <BookOpenIcon className="h-5 w-5" />,
    submenu: [],
  },
  {
    label: "Import Jobs",
    route: "/import-jobs",
    icon: <ArrowUpTrayIcon className="h-5 w-5" />,
    submenu: [],
  },
  
];

export default function NavbarLeft(props: {
  onCollapseChange: (collapsed: boolean) => void;
  isCollapsed: boolean;
}) {
  const user = useContext(UserContext);
  const location = useLocation();
  const [theme, setTheme] = useState(localStorage.getItem("theme"));

  const [navItems, setNavItems] = useState<
    typeof globalAdminNavMenu | typeof businessAdminNavMenu
  >([]);

  useEffect(() => {
    if (user?.["cognito:groups"]?.includes("BUSINESS_GLOBAL_ADMIN")) {
      setNavItems(globalAdminNavMenu);
    } else if (user?.["cognito:groups"]?.includes("BUSINESS_ADMIN")) {
      setNavItems(businessAdminNavMenu);
    } else if (user?.["cognito:groups"]?.includes("BUSINESS_INSTRUCTOR")) {
      setNavItems(businessInstructorNavMenu);
    } else {
      setNavItems([]);
    }
  }, [user]);

  // Helper to check active route with or without collapsed menu
  const isActiveRoute = (route: string) => {
    // match route or prefix
    return (
      location.pathname === route || location.pathname.startsWith(route + "/")
    );
  };

  return (
    <>
      {/* Logo */}
      <div className="min-h-[4rem] flex items-center px-4 ">
        {!props.isCollapsed ? (
          <img
            className="w-40"
            src={`${
              theme === "dark"
                ? "/socora_logo_text.png"
                : "/socora_logo_text_light.png"
            }`}
            alt="Socora Logo"
          />
        ) : (
          <img className="w-7" src="/socora_logo.png" alt="Socora Logo" />
        )}
      </div>

      {/* Navigation */}
      <ul
        className="menu menu-compact rounded-box flex flex-col h-[calc(100%-4rem)] overflow-y-auto "
        aria-label="Main navigation"
      >
        {navItems.map((item, i) => {
          if (!item.submenu || item.submenu.length === 0) {
            return (
              <li key={i}>
                <Link
                  to={item.route}
                  className={`flex items-center gap-2 ${
                    isActiveRoute(item.route)
                      ? "text-primary bg-base-100"
                      : "hover:bg-base-200"
                  }`}
                >
                  {item.icon}
                  {!props.isCollapsed && item.label}
                </Link>
              </li>
            );
          }

          // submenu present
          if (props.isCollapsed) {
            // show only submenu items in collapsed mode (assuming you want this)
            return item.submenu.map((subitem: any, si: number) => (
              <li key={si}>
                <Link
                  to={subitem.route}
                  className={`flex items-center gap-2 ${
                    isActiveRoute(subitem.route) ? "text-primary" : ""
                  }`}
                >
                  {subitem.icon}
                  {!props.isCollapsed && subitem.label}
                </Link>
              </li>
            ));
          } else {
            return (
              <li key={i}>
                <details open={isActiveRoute(item.route)}>
                  <summary className="flex items-center gap-2 cursor-pointer select-none">
                    {item.icon}
                    {!props.isCollapsed && item.label}
                  </summary>
                  <ul className="pl-6 mt-1 space-y-1">
                    {item.submenu.map((subitem: any, si: number) => (
                      <li key={si}>
                        <Link
                          to={subitem.route}
                          className={`flex items-center gap-2 ${
                            isActiveRoute(subitem.route) ? "text-primary" : ""
                          }`}
                        >
                          {subitem.icon}
                          {!props.isCollapsed && subitem.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </details>
              </li>
            );
          }
        })}

        {/* Collapse toggle */}
        <li className="mt-auto">
          <button
            onClick={() => props.onCollapseChange(!props.isCollapsed)}
            aria-label={
              props.isCollapsed ? "Expand navigation" : "Collapse navigation"
            }
            className="flex items-center gap-2 w-full px-4 py-2 hover:bg-base-200 focus:outline-none focus:ring-2 focus:ring-primary rounded"
          >
            {!props.isCollapsed ? (
              <>
                <ArrowLeftIcon className="h-5 w-5" />
                Collapse
              </>
            ) : (
              <ArrowRightIcon className="h-5 w-5" />
            )}
          </button>
        </li>
      </ul>
    </>
  );
}
